import { makeAutoObservable, runInAction } from 'mobx';
import { loadStripe, Stripe, StripeElements, StripeCardNumberElement, StripeCardExpiryElement, StripeCardCvcElement, PaymentMethod as StripePaymentMethod } from '@stripe/stripe-js';
import { apiStore } from './ApiStore';
import { subscriptionStore } from '@/stores/SubscriptionStore';
import { config } from '@/config/env';
import {
  buildAttConversionClickPayload,
  getStoredAttParams,
  resolveAttributionEmail,
  setConversionClickSent,
  wasConversionClickSent,
} from '@/utils/attParams';
import {
  getSubscriptionStatusFromResponse,
  trackSuccessfulPaymentAnalytics,
} from '@/utils/paymentAnalytics';

export type PaymentMethod = 'card' | 'apple';

// Global window interface extensions
declare global {
  interface Window {
    ApplePaySession?: {
      canMakePayments(): boolean;
    };
  }
}

export class PaymentStore {
  selectedPlanId = '';
  processing = false;
  cardNumberElement: StripeCardNumberElement | null = null;
  cardExpiryElement: StripeCardExpiryElement | null = null;
  cardCvcElement: StripeCardCvcElement | null = null;
  elements: StripeElements | null = null;
  private stripe: Stripe | null = null;

  constructor(private api: typeof apiStore) {
    makeAutoObservable(this);
  }

  setPlanId(planId: string): void {
    this.selectedPlanId = planId;
  }

  resetForIdentityChange(): void {
    this.cardNumberElement?.unmount();
    this.cardExpiryElement?.unmount();
    this.cardCvcElement?.unmount();
    this.selectedPlanId = '';
    this.processing = false;
    this.cardNumberElement = null;
    this.cardExpiryElement = null;
    this.cardCvcElement = null;
    this.elements = null;
    this.stripe = null;
  }

  private async getStripe(): Promise<Stripe> {
    if (this.stripe) {
      return this.stripe;
    }

    const stripe = await loadStripe(config.stripePublishableKey);
    if (!stripe) {
      throw new Error('Stripe not configured');
    }

    runInAction(() => {
      this.stripe = stripe;
    });

    return stripe;
  }

  async initializeElements(): Promise<{
    stripe: Stripe;
    elements: StripeElements;
    cardNumberElement: StripeCardNumberElement;
    cardExpiryElement: StripeCardExpiryElement;
    cardCvcElement: StripeCardCvcElement;
  }> {
    const stripe = await this.getStripe();

    // Get setup intent from backend
    const paymentInit = await this.api.initPaymentMethodSelect();
    if (!paymentInit.setup.clientToken) {
      throw new Error('Failed to initialize payment setup');
    }

    // Create Elements instance
    const elements = stripe.elements({
      clientSecret: paymentInit.setup.clientToken,
    });

    const elementStyle = {
      base: {
        'fontSize': '16px',
        'color': '#374151',
        'fontFamily': 'system-ui, sans-serif',
        'backgroundColor': 'transparent',
        '::placeholder': {
          color: '#9ca3af',
        },
      },
      invalid: {
        color: '#ef4444',
        iconColor: '#ef4444',
      },
    };

    // Create separate card elements
    const cardNumberElement = elements.create('cardNumber', {
      style: elementStyle,
      placeholder: 'Card Number',
    });

    const cardExpiryElement = elements.create('cardExpiry', {
      style: elementStyle,
      placeholder: 'MM/YY',
    });

    const cardCvcElement = elements.create('cardCvc', {
      style: elementStyle,
      placeholder: 'CVV',
    });

    runInAction(() => {
      this.elements = elements;
      this.cardNumberElement = cardNumberElement;
      this.cardExpiryElement = cardExpiryElement;
      this.cardCvcElement = cardCvcElement;
    });

    return {
      stripe,
      elements,
      cardNumberElement,
      cardExpiryElement,
      cardCvcElement,
    };
  }

  async processPayment(
    method: PaymentMethod,
    cardholderName?: string,
    planPrice?: string,
  ): Promise<{
    access: {
      accessToken: string;
      refreshToken: string;
      tokenType: string;
      expiresIn: number;
    };
  }> {
    if (!this.selectedPlanId) {
      throw new Error('No plan selected');
    }

    try {
      runInAction(() => {
        this.processing = true;
      });

      const stripe = await this.getStripe();

      // Apple Pay uses different flow
      if (method === 'apple') {
        if (!planPrice) {
          throw new Error('Plan price is required for Apple Pay');
        }
        return await this.processApplePayment(stripe, planPrice);
      }

      // Card payment
      const paymentMethod = await this.processCardPayment(stripe, cardholderName);

      if (!paymentMethod?.id) {
        throw new Error('Failed to create payment method');
      }

      // Create subscription with payment method
      const res = await this.api.createSubscription({
        planId: this.selectedPlanId,
        methodData: { paymentMethodId: paymentMethod.id },
      });

      if (!res) throw new Error('Failed to create subscription');

      const subscription = getSubscriptionStatusFromResponse(res);
      if (subscription?.hasAccess) {
        await trackSuccessfulPaymentAnalytics({
          paymentMethod: 'card',
          subscription,
          planId: this.selectedPlanId,
        });
      }

      return res as unknown as {
        access: {
          accessToken: string;
          refreshToken: string;
          tokenType: string;
          expiresIn: number;
        };
      };
    }
    finally {
      runInAction(() => {
        this.processing = false;
      });
    }
  }

  private async processCardPayment(
    stripe: Stripe,
    cardholderName?: string,
  ): Promise<StripePaymentMethod> {
    if (!this.cardNumberElement) {
      throw new Error('Card elements not initialized');
    }

    // Create payment method using Stripe Elements
    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: 'card',
      card: this.cardNumberElement,
      billing_details: cardholderName
        ? {
            name: cardholderName.trim(),
          }
        : undefined,
    });

    if (error) {
      throw new Error(error.message || 'Payment method creation failed');
    }

    return paymentMethod;
  }

  private async processApplePayment(
    stripe: Stripe,
    planPrice: string,
  ): Promise<{
    access: {
      accessToken: string;
      refreshToken: string;
      tokenType: string;
      expiresIn: number;
    };
  }> {
    // Check if Apple Pay is available
    if (typeof window === 'undefined' || !window.ApplePaySession) {
      throw new Error('Apple Pay not available on this device');
    }

    if (!window.ApplePaySession.canMakePayments()) {
      throw new Error('Apple Pay not configured on this device');
    }

    // Get client secret from initial endpoint
    const planId = this.selectedPlanId || subscriptionStore.selectedTrialPlan?.planId;
    const initResponse = await this.api.initSubscription(
      planId ? { planId } : undefined,
    );
    if (!initResponse.clientToken) {
      throw new Error('Failed to initialize subscription');
    }

    const clientSecret = initResponse.clientToken;

    // Create Elements instance for Apple Pay
    const elements = stripe.elements({
      clientSecret,
      appearance: {
        theme: 'stripe',
        variables: {
          colorPrimary: '#0570de',
        },
      },
    });

    // Create payment element with Apple Pay priority
    const paymentElement = elements.create('payment', {
      paymentMethodOrder: ['apple_pay', 'card'],
      fields: {
        billingDetails: {
          name: 'auto',
          email: 'auto',
        },
      },
    });

    // Check if Apple Pay is available
    const canMakePayment = await stripe
      .paymentRequest({
        country: 'US',
        currency: 'usd',
        total: {
          label: 'Trial Verification',
          amount: Math.round(parseFloat(planPrice) * 100),
        },
        requestPayerName: true,
        requestPayerEmail: true,
      })
      .canMakePayment();

    if (!canMakePayment?.applePay) {
      throw new Error('Apple Pay not available');
    }

    return new Promise((resolve, reject) => {
      // Create a temporary container for the payment element
      const container = document.createElement('div');
      container.style.position = 'fixed';
      container.style.top = '-9999px';
      container.style.left = '-9999px';
      document.body.appendChild(container);

      paymentElement.mount(container);

      // Listen for payment method selection
      paymentElement.on('change', async (event) => {
        if (event.complete && event.value?.type === 'apple_pay') {
          try {
            // Get user email for confirm step
            const userEmail = this.api.user?.email;
            if (!userEmail) {
              reject(new Error('User email is required for subscription confirmation'));
              return;
            }

            // Confirm payment with Apple Pay
            const { error, paymentIntent } = await stripe.confirmPayment({
              elements,
              confirmParams: {
                return_url: window.location.href,
                payment_method_data: {
                  billing_details: {
                    email: userEmail,
                  },
                },
              },
              redirect: 'if_required',
            });

            if (error) {
              throw new Error(
                error instanceof Error ? error.message : String((error as { message?: string }).message ?? 'Payment failed'),
              );
            }

            // Handle 3D Secure if required
            if (paymentIntent?.status === 'requires_action') {
              const { error: actionError } = await stripe.handleCardAction(
                paymentIntent.client_secret!,
              );
              if (actionError) {
                throw new Error(
                  actionError instanceof Error
                    ? actionError.message
                    : String((actionError as { message?: string }).message ?? 'Payment failed'),
                );
              }
            }

            // Confirm subscription: valid att_email, otherwise email from Apple Pay.
            const attParams = getStoredAttParams();
            const emailForConfirm = resolveAttributionEmail(attParams, userEmail) ?? '';
            const clickPayload = buildAttConversionClickPayload(attParams, emailForConfirm);
            if (clickPayload && !wasConversionClickSent(clickPayload)) {
              try {
                await this.api.conversionClick(clickPayload);
                setConversionClickSent(clickPayload);
              }
              catch (error) {
                console.warn('Не удалось отправить conversion/click перед confirmSubscription', error);
              }
            }

            const confirmResponse = await this.api.confirmSubscription({
              email: emailForConfirm,
              paymentData: { paymentIntentId: paymentIntent.id },
              planId,
            });
            await trackSuccessfulPaymentAnalytics({
              paymentMethod: 'apple_pay',
              subscription: confirmResponse.status,
              planId,
            });

            // Clean up
            paymentElement.unmount();
            document.body.removeChild(container);

            resolve(confirmResponse);
          }
          catch (error) {
            // Clean up on error
            paymentElement.unmount();
            document.body.removeChild(container);

            const errorMessage
              = error instanceof Error ? error.message : 'Payment failed';
            reject(new Error(errorMessage));
          }
        }
      });

      // Auto-trigger Apple Pay
      setTimeout(() => {
        const applePayButton = container.querySelector(
          '[data-testid="apple-pay-button"]',
        ) as HTMLElement;
        if (applePayButton) {
          applePayButton.click();
        }
        else {
          reject(new Error('Apple Pay button not found'));
        }
      }, 100);
    });
  }

  clearCard(): void {
    if (this.cardNumberElement) {
      this.cardNumberElement.clear();
    }
    if (this.cardExpiryElement) {
      this.cardExpiryElement.clear();
    }
    if (this.cardCvcElement) {
      this.cardCvcElement.clear();
    }
  }

  destroyElements(): void {
    if (this.cardNumberElement) {
      this.cardNumberElement.unmount();
      runInAction(() => {
        this.cardNumberElement = null;
      });
    }
    if (this.cardExpiryElement) {
      this.cardExpiryElement.unmount();
      runInAction(() => {
        this.cardExpiryElement = null;
      });
    }
    if (this.cardCvcElement) {
      this.cardCvcElement.unmount();
      runInAction(() => {
        this.cardCvcElement = null;
      });
    }
    runInAction(() => {
      this.elements = null;
    });
  }
}

export const paymentStore = new PaymentStore(apiStore);
